import {useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  DrawerHeader,
  Flex,
  Icon,
  IconButton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {CustomizeStepsFlyout} from '@amblique/king-living/app/components/complex-customize/customize-steps-flyout';
import {ConfigurationPanelModePicker} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-mode-picker';
import {PanelContent} from '@amblique/king-living/app/components/configuration-selector/panel-content';
import ReviewSection from '@amblique/king-living/app/components/configuration-selector/review-section';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {focusPdpPanelInitialTarget} from '@amblique/king-living/app/hooks/use-pdp-config-panel-a11y';
import {buildLayerFromProduct, buildLayersFromSelections} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {ConfigurationPanelGalleryBackIcon} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-gallery-back-icon';
import {ConfigurationPanelStepBackIcon} from '@amblique/king-living/app/components/configuration-selector/configuration-panel-step-back-icon';
import {getGalleryDetailShortLabel} from '@amblique/king-living/app/utils/utils';

/** Mobile drawer header — close icon per design (21×21, stroke 0.6 on #1B1A18) */
function ConfigurationPanelMobileCloseIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="21"
      viewBox="0 0 21 21"
      width="21"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 19.75L19.75 1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.6"
        strokeWidth="2"
      />
      <path
        d="M19.75 19.75L1 1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.6"
        strokeWidth="2"
      />
    </svg>
  );
}

/** Panel header + body for desktop and mobile drawer. Requires CustomizeJourneyProvider; mobile needs DrawerContent. */
export const ConfigurationPanel = ({
  complexGalleryState,
  configProducts,
  coverVariantDetailsProduct,
  desktopPanelInitialFocusRef,
  drawerHeight,
  effectiveSelectedId,
  headerTitle,
  isMobile,
  isLoading,
  mobileDrawerInitialFocusRef,
  panelHeaderInfo,
  panelMode,
  product,
  selectedProduct,
  onClose,
  onDone,
  onGalleryProductChange,
  onHeaderChange,
  onSelect,
  onSlotProductPreview,
  onViewDetails,
  setDrawerEscHandler,
  ...props
}) => {
  const intl = useIntl();
  const desktopCloseClipId = `cfg-panel-close-${useId().replace(/:/g, '')}`;
  const mobileCloseButtonRef = useRef(null);
  const mobileBackButtonRef = useRef(null);
  const mobileGalleryBackButtonRef = useRef(null);
  const desktopCloseButtonRef = useRef(null);
  const desktopBackButtonRef = useRef(null);
  const panelRootRef = useRef(null);
  const desktopPanelFocusTokenRef = useRef(null);
  const mobilePanelFocusTokenRef = useRef(null);
  const {
    editSet,
    flyoutDraftState,
    committedSelection,
    setCards,
    setProductsMap,
    switchToCustomizeInFlyout,
  } = useCustomizeJourney();
  const styles = useMultiStyleConfig('ConfigurationSelector', {});

  const isCustomizeMode = panelMode === 'customize';
  const showDesktopModePicker = isCustomizeMode && !panelHeaderInfo;
  const shouldFocusModePickerOnDesktop = !isMobile && showDesktopModePicker;

  const assignDesktopInitialFocusRef = useCallback((node) => {
    if (desktopPanelInitialFocusRef) {
      desktopPanelInitialFocusRef.current = node;
    }
  }, [desktopPanelInitialFocusRef]);

  const syncDesktopPanelInitialFocusRef = useCallback((node) => {
    if (desktopPanelInitialFocusRef) {
      desktopPanelInitialFocusRef.current = node;
    }
  }, [desktopPanelInitialFocusRef]);

  const getDesktopInitialFocusRef = useCallback(() => {
    if (panelHeaderInfo?.onBack) return desktopBackButtonRef;

    if (showDesktopModePicker) return desktopPanelInitialFocusRef;

    return desktopCloseButtonRef;
  }, [desktopPanelInitialFocusRef, panelHeaderInfo?.onBack, showDesktopModePicker]);

  useEffect(() => {
    desktopPanelFocusTokenRef.current = null;
    mobilePanelFocusTokenRef.current = null;

    return () => {
      desktopPanelFocusTokenRef.current = null;
      mobilePanelFocusTokenRef.current = null;
    };
  }, []);

  const [galleryViewState, setGalleryViewState] = useState(null);

  const inlineCustomizeGalleryProduct = useMemo(
    () => complexGalleryState?.latestHeroProduct ?? product,
    [complexGalleryState?.latestHeroProduct, product]
  );

  const coverVariationForGallery = complexGalleryState?.galleryVariationAttributes ?? {};

  const customizeGalleryLayers = useMemo(() => {
    if (panelMode === 'customize') return complexGalleryState?.galleryLayers ?? [];

    return buildLayersFromSelections(
      flyoutDraftState?.draftSelections ?? committedSelection?.selections
    );
  }, [
    complexGalleryState,
    panelMode,
    flyoutDraftState?.draftSelections,
    committedSelection?.selections,
  ]);

  const galleryOverlayLayers = useMemo(() => {
    if (!galleryViewState?.product) return customizeGalleryLayers;

    return [...customizeGalleryLayers, buildLayerFromProduct(galleryViewState.product)];
  }, [customizeGalleryLayers, galleryViewState?.product]);

  const handleOpenGalleryView = useCallback((viewProductOrId, options = {}) => {
    if (!viewProductOrId) return;

    const {shelvesDetailUi = false, coverItemType} = options;
    const viewProduct = typeof viewProductOrId === 'string' ?
      (configProducts?.find((p) => p.id === viewProductOrId || p.productId === viewProductOrId) ?? null) :
      viewProductOrId;

    if (!viewProduct) return;

    const clickedVariation = viewProduct._selectedVariationValues;

    setGalleryViewState({
      product: viewProduct,
      selectedVariationAttributes: panelMode === 'customize' ?
        (clickedVariation && Object.keys(clickedVariation).length > 0 ?
          clickedVariation :
          coverVariationForGallery) :
        undefined,
      shelvesDetailUi: Boolean(shelvesDetailUi),
      coverItemType: typeof coverItemType === 'string' && coverItemType.trim() !== '' ?
        coverItemType.trim() :
        undefined,
    });
  }, [configProducts, panelMode, coverVariationForGallery]);

  const handleCloseGalleryView = useCallback(() => {
    setGalleryViewState(null);
  }, []);

  useEffect(() => {
    if (typeof setDrawerEscHandler !== 'function') return undefined;

    setDrawerEscHandler(() => {
      if (galleryViewState) {
        handleCloseGalleryView();

        return true;
      }

      if (panelHeaderInfo?.onBack) {
        panelHeaderInfo.onBack();

        return true;
      }

      return false;
    });

    return () => setDrawerEscHandler(null);
  }, [galleryViewState, handleCloseGalleryView, panelHeaderInfo, setDrawerEscHandler]);

  useLayoutEffect(() => {
    if (!isMobile) return undefined;

    const focusToken = galleryViewState ?
      'gallery' :
      panelHeaderInfo?.onBack ?
        `sub:${panelHeaderInfo.title ?? ''}` :
        isCustomizeMode ?
          'customize-root' :
          'choose-layout';

    if (mobilePanelFocusTokenRef.current === focusToken) return undefined;

    mobilePanelFocusTokenRef.current = focusToken;

    const id = requestAnimationFrame(() => {
      let focusTarget = null;

      if (galleryViewState) {
        focusTarget = mobileGalleryBackButtonRef.current;
      } else if (panelHeaderInfo?.onBack) {
        focusTarget = mobileBackButtonRef.current;
      } else if (isCustomizeMode) {
        const firstChooseButton = panelRootRef.current?.querySelector(
          '.slot-card-choose-button:not([disabled])'
        );

        focusTarget = firstChooseButton ?? mobileCloseButtonRef.current;
      } else {
        focusTarget = mobileCloseButtonRef.current;
      }

      if (mobileDrawerInitialFocusRef) {
        mobileDrawerInitialFocusRef.current = focusTarget;
      }

      focusTarget?.focus({preventScroll: true});
    });

    return () => cancelAnimationFrame(id);
  }, [
    galleryViewState,
    isCustomizeMode,
    isMobile,
    panelHeaderInfo?.onBack,
    panelHeaderInfo?.title,
    mobileDrawerInitialFocusRef,
  ]);

  useLayoutEffect(() => {
    if (isMobile) return undefined;

    // Scroll-synced step titles on the main customize list must not move keyboard focus.
    if (isCustomizeMode && panelHeaderInfo && !panelHeaderInfo.onBack) {
      return undefined;
    }

    const focusToken = panelHeaderInfo?.onBack ?
      `sub:${panelHeaderInfo.title ?? ''}` :
      showDesktopModePicker ?
        'customize-root-mode-picker' :
        isCustomizeMode ?
          'customize-root-close' :
          'choose-layout';

    if (desktopPanelFocusTokenRef.current === focusToken) return undefined;

    desktopPanelFocusTokenRef.current = focusToken;
    focusPdpPanelInitialTarget(getDesktopInitialFocusRef(), panelRootRef);

    return undefined;
  }, [
    getDesktopInitialFocusRef,
    isCustomizeMode,
    isMobile,
    panelHeaderInfo?.onBack,
    panelHeaderInfo?.title,
    panelMode,
    showDesktopModePicker,
  ]);

  const handleExpandCustomizeGallery = useCallback(() => {
    handleOpenGalleryView(inlineCustomizeGalleryProduct);
  }, [handleOpenGalleryView, inlineCustomizeGalleryProduct]);

  const handleExpandSelectedGallery = useCallback(() => {
    handleOpenGalleryView(selectedProduct);
  }, [handleOpenGalleryView, selectedProduct]);

  const currentProductId = product?.id ?? product?.productId;

  // In customize mode, derive the picker's effectiveSelectedId from the context's
  // editingProductId so the dropdown reflects the active set even when opened via cards.
  // When the user deviates from the set, force to the master product ID so the dropdown
  // shows "Customise your set" instead of the predefined set name.
  const pickerEffectiveId = useMemo(() => {
    if (isCustomizeMode && flyoutDraftState.isOpen) {
      if (flyoutDraftState.isDeviatingFromSet) {
        return currentProductId;
      }

      const editingId = flyoutDraftState.editingProductId;

      // Any editingId that differs from the master is a set product.
      // Avoids a setProductsMap lookup that can miss when the map reference
      // hasn't settled after a React Query cache update.
      if (editingId && editingId !== currentProductId) {
        return editingId;
      }
    }

    return effectiveSelectedId;
  }, [currentProductId, effectiveSelectedId, flyoutDraftState, isCustomizeMode]);

  // Handle dropdown selection: switch between pre-defined sets and free customize.
  const handleModePickerSelect = useCallback(
    (productId) => {
      if (isCustomizeMode && flyoutDraftState.isOpen) {
        const isSetProduct = productId && setProductsMap[productId];

        if (isSetProduct) {
          editSet(productId);
        } else {
          switchToCustomizeInFlyout();
        }
      }

      onSelect?.(productId);
    },
    [editSet, flyoutDraftState.isOpen, isCustomizeMode, onSelect, setProductsMap, switchToCustomizeInFlyout]
  );

  const mobileViewDetails = isMobile ? handleOpenGalleryView : onViewDetails;
  const panelContentViewDetails =
    !isMobile && panelMode === 'choose-layout' ? undefined : mobileViewDetails;

  const galleryDetailsHeaderLabel = useMemo(
    () => getGalleryDetailShortLabel(galleryViewState?.product),
    [
      galleryViewState?.product?.id,
      galleryViewState?.product?.shortDescription,
      galleryViewState?.product?.c_nameLine2,
    ]
  );

  const galleryViewTitle = useMemo(() => {
    if (galleryViewState?.coverItemType) {
      return intl.formatMessage(
        {
          defaultMessage: '{label} details',
          id: 'configurationPanel.galleryDetailsTitleWithLabel',
        },
        {label: galleryViewState.coverItemType}
      );
    }

    if (galleryViewState?.shelvesDetailUi && galleryDetailsHeaderLabel) {
      return intl.formatMessage(
        {
          defaultMessage: '{label} details',
          id: 'configurationPanel.galleryDetailsTitleWithLabel',
        },
        {label: galleryDetailsHeaderLabel}
      );
    }

    return intl.formatMessage({
      defaultMessage: 'View layout details',
      id: 'configurationPanel.galleryDetailsTitle',
    });
  }, [
    galleryViewState?.coverItemType,
    galleryViewState?.shelvesDetailUi,
    galleryDetailsHeaderLabel,
    intl,
  ]);

  const ariaBackToConfiguration = intl.formatMessage({
    defaultMessage: 'Back to configuration',
    id: 'configurationPanel.aria.backToConfiguration',
  });
  const ariaBackToSteps = intl.formatMessage({
    defaultMessage: 'Back to steps',
    id: 'configurationPanel.aria.backToSteps',
  });
  const ariaClosePanel = intl.formatMessage({
    defaultMessage: 'Close configuration panel',
    id: 'configurationPanel.aria.closePanel',
  });
  const ariaExpandGallery = intl.formatMessage({
    defaultMessage: 'Expand gallery',
    id: 'configurationPanel.aria.expandGallery',
  });

  const mobileStepBackButton = panelHeaderInfo?.onBack ? (
    <IconButton
      ref={(node) => {
        mobileBackButtonRef.current = node;

        if (mobileDrawerInitialFocusRef && !galleryViewState) {
          mobileDrawerInitialFocusRef.current = node;
        }
      }}
      aria-label={ariaBackToSteps}
      icon={<ConfigurationPanelStepBackIcon />}
      {...styles.mobileStepBackButton}
      onClick={panelHeaderInfo.onBack}
    />
  ) : null;

  const desktopStepBackButton = panelHeaderInfo?.onBack ? (
    <IconButton
      ref={(node) => {
        desktopBackButtonRef.current = node;
        syncDesktopPanelInitialFocusRef(node);
      }}
      aria-label={ariaBackToSteps}
      icon={<ConfigurationPanelStepBackIcon />}
      {...styles.desktopPanelBackButton}
      onClick={panelHeaderInfo.onBack}
    />
  ) : null;

  const stepTitle = panelHeaderInfo ? (
    <Flex {...styles.panelHeaderTitleRow} className="panel-header-title-row" width="100%">
      <Text
        className="panel-header-title"
        {...(isMobile && panelHeaderInfo.chooseYourShelvesStep ?
          styles.panelHeaderTitleLabelShelves :
          styles.panelHeaderTitleLabel)}
        noOfLines={1}
      >
        {panelHeaderInfo.title}
      </Text>
      {panelHeaderInfo.stepIndex != null && panelHeaderInfo.totalSteps != null && (
        <Text className="panel-header-step" {...styles.panelHeaderStepIndicator}>
          {`${panelHeaderInfo.stepIndex}/${panelHeaderInfo.totalSteps}`}
        </Text>
      )}
    </Flex>
  ) : null;

  const modePicker = (
    <ConfigurationPanelModePicker
      currentProductId={currentProductId}
      effectiveSelectedId={pickerEffectiveId}
      initialFocusRef={shouldFocusModePickerOnDesktop ? assignDesktopInitialFocusRef : undefined}
      listSetProducts={setCards}
      useModalForPicker={Boolean(isMobile)}
      onSelect={handleModePickerSelect}
    />
  );

  // Step kind (cover / shelves / …) only when picker flyout is open — `onBack` is set in that state,
  // not when the user only scrolls the main steps list (header still shows step title).
  const showCustomizeStepKindModifier =
    Boolean(panelHeaderInfo?.onBack && panelHeaderInfo?.customizeStepKind);

  const desktopHeaderModeClass = panelHeaderInfo ? [
    'configuration-panel-header--customize-step',
    showCustomizeStepKindModifier &&
      `configuration-panel-header--customize-step--${panelHeaderInfo.customizeStepKind}`,
  ].filter(Boolean).join(' ') :
    (isCustomizeMode ? 'configuration-panel-header--customize-root' : 'configuration-panel-header--choose-layout');

  const desktopPanelCloseIcon = (
    <Icon fill="none" h={5} viewBox="0 0 20 20" w={5}>
      <g clipPath={`url(#${desktopCloseClipId})`}>
        <path
          d="M0.625 19.375L19.375 0.625"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
        <path
          d="M19.375 19.375L0.625 0.625"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </g>
      <defs>
        <clipPath id={desktopCloseClipId}>
          <rect fill="white" height="20" width="20" />
        </clipPath>
      </defs>
    </Icon>
  );

  const desktopPanelCloseButton = (
    <IconButton
      ref={(node) => {
        desktopCloseButtonRef.current = node;

        if (!panelHeaderInfo?.onBack && !shouldFocusModePickerOnDesktop) {
          syncDesktopPanelInitialFocusRef(node);
        } else if (shouldFocusModePickerOnDesktop) {
          syncDesktopPanelInitialFocusRef(null);
        }
      }}
      flexShrink={0}
      marginLeft="auto"
      size="xs"
      {...styles.desktopPanelCloseButton}
      aria-label={ariaClosePanel}
      icon={desktopPanelCloseIcon}
      onClick={onClose}
    />
  );

  const header = isMobile ? (
    galleryViewState ? (
      <DrawerHeader
        className={[
          'configuration-panel-header',
          'configuration-panel-header--gallery-detail',
          (galleryViewState.shelvesDetailUi || galleryViewState.coverItemType) ?
            'configuration-panel-header--gallery-shelves-detail' :
            'configuration-panel-header--gallery-layout',
        ].join(' ')}
        {...styles.galleryViewHeader}
        borderBottomWidth={0}
        borderWidth={0}
      >
        <Flex alignItems="center" gap={4} width="100%">
          <IconButton
            ref={mobileGalleryBackButtonRef}
            {...styles.galleryViewBackButton}
            aria-label={ariaBackToConfiguration}
            icon={<ConfigurationPanelGalleryBackIcon />}
            onClick={handleCloseGalleryView}
          />
          <Text
            {...(galleryViewState?.coverItemType ?
              styles.galleryViewHeaderTitleCoverDetail :
              galleryViewState?.shelvesDetailUi ?
                styles.galleryViewHeaderTitleShelvesDetail :
                styles.galleryViewHeaderTitle)}
            noOfLines={1}
          >
            {galleryViewTitle}
          </Text>
        </Flex>
      </DrawerHeader>
    ) : isCustomizeMode && panelHeaderInfo ? (
      <DrawerHeader
        className={[
          'configuration-panel-header',
          'configuration-panel-header--customize-step',
          showCustomizeStepKindModifier &&
            `configuration-panel-header--customize-step--${panelHeaderInfo.customizeStepKind}`,
        ].filter(Boolean).join(' ')}
        {...styles.header}
        borderBottomWidth={0}
        borderWidth={0}
      >
        <Flex alignItems="center" gap={4} width="100%">
          {mobileStepBackButton}
          {stepTitle}
        </Flex>
      </DrawerHeader>
    ) : (
      <DrawerHeader
        className={`configuration-panel-header ${isCustomizeMode ? 'configuration-panel-header--customize-root' : 'configuration-panel-header--choose-layout'}`}
        {...styles.header}
        borderBottomWidth={0}
        borderWidth={0}
      >
        <Flex alignItems="center" gap={3} justifyContent="space-between" width="100%">
          {isCustomizeMode ? (
            <Box flex="1" minWidth={0}>
              {modePicker}
            </Box>
          ) : (
            <Text {...styles.headerTitle} flex="1" minWidth={0} noOfLines={1}>
              {headerTitle}
            </Text>
          )}
          <IconButton
            ref={(node) => {
              mobileCloseButtonRef.current = node;

              if (mobileDrawerInitialFocusRef && !galleryViewState && !panelHeaderInfo?.onBack && !isCustomizeMode) {
                mobileDrawerInitialFocusRef.current = node;
              }
            }}
            {...styles.closeButton}
            aria-label={ariaClosePanel}
            icon={<ConfigurationPanelMobileCloseIcon />}
            onClick={onClose}
          />
        </Flex>
      </DrawerHeader>
    )
  ) : (
    <Box
      alignItems="center"
      className={`configuration-selector-panel__header-desktop configuration-panel-header ${desktopHeaderModeClass}`}
      display="flex"
      {...styles.desktopPanelHeader}
      gap="16px"
      justifyContent="flex-start"
      width="100%"
    >
      {panelHeaderInfo ? (
        <>
          {desktopStepBackButton}
          {stepTitle}
        </>
      ) : (
        <>
          {isCustomizeMode ? modePicker : (
            <Text {...styles.headerTitle}>{headerTitle}</Text>
          )}
        </>
      )}
      {desktopPanelCloseButton}
    </Box>
  );

  const mobileCustomizeGalleryHeader = isMobile && isCustomizeMode ? (
    <Box {...styles.mobileImageHeader}>
      <ReviewSection
        forceCylindoCheck
        isThumbnailZoomEnabled={panelMode !== 'choose-layout'}
        layers={customizeGalleryLayers}
        masterProduct={product}
        previewProduct={null}
        selections={committedSelection?.selections}
        selectedVariationAttributes={coverVariationForGallery}
        showViewDetailsSection={false}
        viewDetailsProduct={coverVariantDetailsProduct}
      />
      <IconButton
        {...styles.mobileExpandButton}
        aria-label={ariaExpandGallery}
        icon={
          <Icon viewBox="0 0 24 24">
            <path
              d="M3 3h7v2H5v5H3V3zm11 0h7v7h-2V5h-5V3zM3 14h2v5h5v2H3v-7zm16 5h-5v2h7v-7h-2v5z"
              fill="currentColor"
            />
          </Icon>
        }
        onClick={handleExpandCustomizeGallery}
      />
    </Box>
  ) : null;

  const body = isCustomizeMode ? (
    <CustomizeStepsFlyout
      drawerHeight={drawerHeight}
      product={product}
      onGalleryProductChange={onGalleryProductChange}
      onHeaderChange={onHeaderChange}
      onSlotProductPreview={onSlotProductPreview}
      onViewDetails={mobileViewDetails}
    />
  ) : (
    <PanelContent
      configProducts={configProducts}
      isLoading={isLoading}
      selectedId={(selectedProduct?.productId || selectedProduct?.id) ?? effectiveSelectedId}
      selectedProduct={selectedProduct}
      showMobileImageHeader={Boolean(isMobile)}
      onDone={onDone}
      onExpandGallery={isMobile ? handleExpandSelectedGallery : undefined}
      onSelect={onSelect}
      onViewDetails={panelContentViewDetails}
    />
  );

  const isGalleryViewOpen = Boolean(isMobile && galleryViewState);

  const configurationPanelRootClassName = [
    'configuration-panel__root',
    galleryViewState ?
      'configuration-panel__root--gallery-detail' :
      panelHeaderInfo && isCustomizeMode ?
        'configuration-panel__root--customize-step' :
        isCustomizeMode ?
          'configuration-panel__root--customize-root' :
          'configuration-panel__root--choose-layout',
  ].join(' ');

  const configurationPanelBodyWrapperClassName = [
    'configuration-panel-body-wrapper',
    isCustomizeMode &&
      showCustomizeStepKindModifier &&
      `configuration-panel-body-wrapper--customize-step--${panelHeaderInfo.customizeStepKind}`,
  ].filter(Boolean).join(' ');

  return (
    <Box
      ref={panelRootRef}
      className={configurationPanelRootClassName}
      {...styles.configurationPanelRoot}
      {...props}
    >
      {header}
      {isGalleryViewOpen && (
        <Box className="gallery-overlay-content" {...styles.galleryOverlayContent}>
          <ReviewSection
            key={galleryViewState.product?.id ?? 'gallery-preview'}
            forceCylindoCheck={isCustomizeMode}
            imageZoomFullScreen
            isThumbnailZoomEnabled={panelMode !== 'choose-layout'}
            layers={isCustomizeMode ? galleryOverlayLayers : undefined}
            masterProduct={isCustomizeMode ? product : galleryViewState.product}
            previewProduct={null}
            selections={committedSelection?.selections}
            selectedVariationAttributes={galleryViewState.selectedVariationAttributes}
            showMobileConfigThumbnails={!isCustomizeMode}
            showViewDetailsSection={effectiveSelectedId != null}
            hideImageGalleryOverlayButtons
            stickyGallery
          />
        </Box>
      )}

      <Box
        className={configurationPanelBodyWrapperClassName}
        display={isGalleryViewOpen ? 'none' : 'flex'}
        flex={isGalleryViewOpen ? undefined : 1}
        flexDirection="column"
        overflow="hidden"
      >
        {mobileCustomizeGalleryHeader}
        {body}
      </Box>
    </Box>
  );
};

ConfigurationPanel.displayName = 'ConfigurationPanel';

ConfigurationPanel.propTypes = {
  complexGalleryState: PropTypes.object,
  configProducts: PropTypes.array,
  coverVariantDetailsProduct: PropTypes.object,
  desktopPanelInitialFocusRef: PropTypes.shape({current: PropTypes.any}),
  mobileDrawerInitialFocusRef: PropTypes.shape({current: PropTypes.any}),
  drawerHeight: PropTypes.number,
  effectiveSelectedId: PropTypes.string,
  headerTitle: PropTypes.string,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  panelHeaderInfo: PropTypes.shape({
    chooseYourShelvesStep: PropTypes.bool,
    customizeStepKind: PropTypes.string,
    onBack: PropTypes.func,
    stepIndex: PropTypes.number,
    title: PropTypes.node,
    totalSteps: PropTypes.number,
  }),
  panelMode: PropTypes.string,
  product: PropTypes.object.isRequired,
  selectedProduct: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onGalleryProductChange: PropTypes.func,
  onHeaderChange: PropTypes.func,
  onSelect: PropTypes.func,
  onSlotProductPreview: PropTypes.func,
  onViewDetails: PropTypes.func,
  setDrawerEscHandler: PropTypes.func,
};
